import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import Parameter from "../../parameter/Parameter";
import PortalDocument from "../../../document/PortalDocument";
import {Button} from "primereact/button";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import DialogDefault from "../../parameter/PortalDialogDefault";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import {Dropdown} from "primereact/dropdown";
import {ColorPicker} from "primereact/colorpicker";
import {InputNumber} from "primereact/inputnumber";
import validator from "validator";
import MimeType from "../../../document/mimetype/MimeType";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {
	AdminWebserviceStatus,
	BaseToolbox,
	Functions,
	Information,
	Security,
	Settings,
	SettingsInterface,
	SignatureEditProfile,
	TextAnnotation,
	TextAnnotationIcons,
	ToolboxAnnotationAnnotation
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import AnnotationNoteDialogFields from "../../field/annotation/AnnotationNoteDialogFields";
import Icon from "../../../../shared/components/Icon";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";

export class AnnotationNoteEditDialogDefinition extends PortalDialog<ToolboxAnnotationAnnotation,
	PortalDialogProperties<ToolboxAnnotationAnnotation>, PortalDialogState<ToolboxAnnotationAnnotation>> {
	constructor(props: any) {
		super(props);

		this.state = {
			...this.state,
			fieldErrors: new Map<AnnotationNoteDialogFields, string>()
		};
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.toolbox?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.editAnnotation, Functions.getEditAnnotationDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.ANNOTATION_NOTE_EDIT, signatureEditProfile, Intention.editingAnnotations,
				selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable()) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["annotation-note-edit-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					header={this.renderHeader()} footer={this.renderFooter()}
					appendTo={document.getElementById("root")} resizable={false}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.annotation_note.edit"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	public renderContents(): ReactNode {
		let parameter: ToolboxAnnotationAnnotation | undefined = this.state.parameter?.data;
		let errors: Map<AnnotationNoteDialogFields, string> = this.state.fieldErrors;

		if (!FunctionHelper.exists(parameter?.edit?.text)) {
			return null;
		}

		let annotationParameter: TextAnnotation = parameter!.edit!.text!;

		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(AnnotationNoteDialogFields.CREATOR)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-creator">
							<Translate {...this.props} data="annotation.properties.creator.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<InputText id="control-creator"
									   className={errors.has(AnnotationNoteDialogFields.CREATOR) ? "p-invalid" : ""}
									   tooltip={errors.has(AnnotationNoteDialogFields.CREATOR) ? Translate.value(
										   errors.get(AnnotationNoteDialogFields.CREATOR)!, {ns: "message"}
									   ) : ""}
									   value={FunctionHelper.applyValue(
										   annotationParameter.creator, TextAnnotation.getCreatorDefault()
									   )}
									   onInput={(e) => this.onChange(
										   AnnotationNoteDialogFields.CREATOR, e.currentTarget.value
									   )}/>
							{this.renderFieldError(errors.has(AnnotationNoteDialogFields.CREATOR) ?
								Translate.value(
									errors.get(AnnotationNoteDialogFields.CREATOR)!, {ns: "message"}
								) : ""
							)}
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(AnnotationNoteDialogFields.SUBJECT)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-subject">
							<Translate {...this.props} data="annotation.properties.subject.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<InputText id="control-subject"
									   value={FunctionHelper.applyValue(
										   annotationParameter.subject, TextAnnotation.getSubjectDefault()
									   )}
									   onInput={(e) => this.onChange(
										   AnnotationNoteDialogFields.SUBJECT, e.currentTarget.value
									   )}/>
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(AnnotationNoteDialogFields.CONTENTS)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-contents">
							<Translate {...this.props} data="annotation.properties.content.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<InputTextarea rows={3} id="control-contents"
										   value={FunctionHelper.applyValue(
											   annotationParameter.contents, TextAnnotation.getContentsDefault()
										   )}
										   onInput={(e) => this.onChange(
											   AnnotationNoteDialogFields.CONTENTS, e.currentTarget.value
										   )}/>
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(AnnotationNoteDialogFields.INTENTS)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-intents">
							<Translate {...this.props} data="annotation.properties.intents" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<InputText id="control-intents"
									   value={FunctionHelper.applyValue(
										   annotationParameter.intents, TextAnnotation.getIntentsDefault()
									   )}
									   onInput={(e) => this.onChange(
										   AnnotationNoteDialogFields.INTENTS, e.currentTarget.value
									   )}/>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(AnnotationNoteDialogFields.ICON)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-icon">
							<Translate {...this.props} data="annotation.appearance.icon.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<Dropdown inputId="control-icon"
									  value={FunctionHelper.applyValue(
										  annotationParameter.icon, TextAnnotation.getIconDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "annotation.appearance.icon.comment", {ns: "dialog"}
											  ),
											  value: TextAnnotationIcons.Comment
										  },
										  {
											  label: Translate.value(
												  "annotation.appearance.icon.help", {ns: "dialog"}
											  ),
											  value: TextAnnotationIcons.Help
										  },
										  {
											  label: Translate.value(
												  "annotation.appearance.icon.insert", {ns: "dialog"}
											  ),
											  value: TextAnnotationIcons.Insert
										  },
										  {
											  label: Translate.value(
												  "annotation.appearance.icon.key", {ns: "dialog"}
											  ),
											  value: TextAnnotationIcons.Key
										  },
										  {
											  label: Translate.value(
												  "annotation.appearance.icon.new_paragraph", {ns: "dialog"}
											  ),
											  value: TextAnnotationIcons.NewParagraph
										  },
										  {
											  label: Translate.value(
												  "annotation.appearance.icon.note", {ns: "dialog"}
											  ),
											  value: TextAnnotationIcons.Note
										  },
										  {
											  label: Translate.value(
												  "annotation.appearance.icon.paragraph", {ns: "dialog"}
											  ),
											  value: TextAnnotationIcons.Paragraph
										  },
										  {
											  label: Translate.value(
												  "annotation.appearance.icon.check", {ns: "dialog"}
											  ),
											  value: TextAnnotationIcons.Check
										  },
										  {
											  label: Translate.value(
												  "annotation.appearance.icon.checkmark", {ns: "dialog"}
											  ),
											  value: TextAnnotationIcons.Checkmark
										  },
										  {
											  label: Translate.value(
												  "annotation.appearance.icon.circle", {ns: "dialog"}
											  ),
											  value: TextAnnotationIcons.Circle
										  },
										  {
											  label: Translate.value(
												  "annotation.appearance.icon.cross", {ns: "dialog"}
											  ),
											  value: TextAnnotationIcons.Cross
										  },
										  {
											  label: Translate.value(
												  "annotation.appearance.icon.crosshair", {ns: "dialog"}
											  ),
											  value: TextAnnotationIcons.CrossHair
										  },
										  {
											  label: Translate.value(
												  "annotation.appearance.icon.right_arrow", {ns: "dialog"}
											  ),
											  value: TextAnnotationIcons.RightArrow
										  },
										  {
											  label: Translate.value(
												  "annotation.appearance.icon.right_pointer", {ns: "dialog"}
											  ),
											  value: TextAnnotationIcons.RightPointer
										  },
										  {
											  label: Translate.value(
												  "annotation.appearance.icon.star", {ns: "dialog"}
											  ),
											  value: TextAnnotationIcons.Star
										  },
										  {
											  label: Translate.value(
												  "annotation.appearance.icon.up_arrow", {ns: "dialog"}
											  ),
											  value: TextAnnotationIcons.UpArrow
										  },
										  {
											  label: Translate.value(
												  "annotation.appearance.icon.up_left_arrow", {ns: "dialog"}
											  ),
											  value: TextAnnotationIcons.UpLeftArrow
										  },
									  ]}
									  onChange={(e) => this.onChange(AnnotationNoteDialogFields.ICON, e.value)}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(AnnotationNoteDialogFields.COLOR)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-color">
							<Translate {...this.props} data="annotation.appearance.color.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8">
							<div className={"p-inputgroup" + (
								errors.has(AnnotationNoteDialogFields.COLOR) ? " p-invalid" : ""
							)}>
								<InputText className={errors.has(AnnotationNoteDialogFields.COLOR) ? "p-invalid" : ""}
										   tooltip={errors.has(AnnotationNoteDialogFields.COLOR) ? Translate.value(
											   errors.get(AnnotationNoteDialogFields.COLOR)!, {ns: "message"}
										   ) : ""}
										   value={FunctionHelper.applyValue(
											   annotationParameter.color, TextAnnotation.getColorDefault()
										   )}
										   onInput={(e) => this.onChange(
											   AnnotationNoteDialogFields.COLOR, e.currentTarget.value
										   )}/>
								<ColorPicker inputId="control-color" className="p-inputgroup-addon" format="hex"
											 defaultColor={
												 TextAnnotation.getColorDefault().replace("#", "")
											 }
											 value={FunctionHelper.applyValue(
												 annotationParameter.color, TextAnnotation.getColorDefault()
											 ).replace("#", "")}
											 onChange={(e) => this.onChange(
												 AnnotationNoteDialogFields.COLOR, "#" + e.value
											 )}
											 appendTo={document.getElementById("root")}/>
							</div>
							{this.renderFieldError(errors.has(AnnotationNoteDialogFields.COLOR) ?
								Translate.value(
									errors.get(AnnotationNoteDialogFields.COLOR)!, {ns: "message"}
								) : ""
							)}
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(AnnotationNoteDialogFields.OPACITY)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-opacity">
							<Translate {...this.props} data="annotation.appearance.opacity" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8">
							<InputNumber inputId="control-opacity" step={1} suffix=" %" showButtons={true}
										 useGrouping={false}
										 className={errors.has(AnnotationNoteDialogFields.OPACITY) ? "p-invalid" : ""}
										 tooltip={errors.has(AnnotationNoteDialogFields.OPACITY) ? Translate.value(
											 errors.get(AnnotationNoteDialogFields.OPACITY)!, {ns: "message"}
										 ) : ""}
										 value={FunctionHelper.applyValue(
											 annotationParameter.opacity, TextAnnotation.getOpacityDefault()
										 )}
										 min={TextAnnotation.getOpacityMin()} max={TextAnnotation.getOpacityMax()}
										 onChange={(e) => this.onChange(
											 AnnotationNoteDialogFields.OPACITY, e.value
										 )}/>
							{this.renderFieldError(errors.has(AnnotationNoteDialogFields.OPACITY) ?
								Translate.value(
									errors.get(AnnotationNoteDialogFields.OPACITY)!, {ns: "message"}
								) : ""
							)}
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	protected onChange(field: AnnotationNoteDialogFields, value: any): void {
		let parameter: ToolboxAnnotationAnnotation = this.state.parameter!.data;
		let annotationParameter: TextAnnotation = parameter.edit!.text!;
		let errors: Map<AnnotationNoteDialogFields, string> = this.state.fieldErrors;

		switch (field) {
			case AnnotationNoteDialogFields.CREATOR:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				annotationParameter.creator = value;
				break;
			case AnnotationNoteDialogFields.SUBJECT:
				annotationParameter.subject = value;
				break;
			case AnnotationNoteDialogFields.CONTENTS:
				annotationParameter.contents = value;
				break;
			case AnnotationNoteDialogFields.INTENTS:
				annotationParameter.intents = value;
				break;
			case AnnotationNoteDialogFields.ICON:
				annotationParameter.icon = value;
				break;
			case AnnotationNoteDialogFields.COLOR:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (!validator.isHexColor(value)) {
					errors.set(field, "errors.fields.color");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				annotationParameter.color = value;
				break;
			case AnnotationNoteDialogFields.OPACITY:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = TextAnnotation.getOpacityDefault();
				}

				annotationParameter.opacity = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		});
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-edit"}/>
					<Translate {...this.props} data="annotation.edit" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected isEditPasswordNeeded(portalDocument: PortalDocument): boolean {
		return super.isEditPasswordNeeded(portalDocument) && !FunctionHelper.applyValue(
			portalDocument.metadata?.security?.canModifyAnnotations, Security.getCanModifyAnnotationsDefault()
		);
	}

	protected async submit(): Promise<void> {
		await super.submit();

		let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
			annotation: this.state.parameter!.data.toJson()
		});

		let operationData: Array<BaseToolbox> = [toolboxOperation];
		let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		for (let portalDocument of selection) {
			try {
				await this.props.context.session.executeRequest({
					webServiceType: webServiceType,
					document: portalDocument,
					operation: operationData,
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as SettingsInterface)
				});
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await this.submit();
				}
				return;
			}

			let message: Message = new Message("success.toolbox.annotation.edit.note", MessageType.SUCCESS);
			message.context = {filename: '"' + portalDocument.getFileName() + '" '};
			this.props.context.getPage().addMessage(message);
			this.hide();
		}
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.ANNOTATION_NOTE_EDIT;
	}

	protected getDefaultParameter(): Parameter<ToolboxAnnotationAnnotation> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}
}

const AnnotationNoteEditDialog = React.memo(AnnotationNoteEditDialogDefinition,
	(
		prevProps: Readonly<PortalDialogProperties<ToolboxAnnotationAnnotation>>,
		nextProps: Readonly<PortalDialogProperties<ToolboxAnnotationAnnotation>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default AnnotationNoteEditDialog;